import {
  NUMBER_FORGE_PLACE_VALUES,
  decomposeNumberForgeValue,
  validateNumberForgeConfig,
  type NumberForgeConfig,
  type NumberForgePlace,
  type NumberForgePlaceCounts,
} from './NumberForgeConfig.js';

export type NumberForgeSource = 'a' | 'b';
export type NumberForgePhase = 'discovering' | 'sorting' | 'regrouping' | 'ready-to-charge' | 'complete';
export type NumberForgeMoveResult =
  | 'accepted'
  | 'ready'
  | 'invalid'
  | 'empty-source'
  | 'source-hidden'
  | 'complete';
export type NumberForgeChargeResult =
  | 'complete'
  | 'open-number-ingots'
  | 'combine-more-tens'
  | 'combine-more-ones'
  | 'combine-more-hundreds'
  | 'regroup-place-values'
  | 'check-place-values';

export const NUMBER_FORGE_FEEDBACK: Readonly<Record<Exclude<NumberForgeChargeResult, 'complete'>, string>> = {
  'open-number-ingots': 'Open both number-energy ingots first.',
  'combine-more-hundreds': 'Check how many hundreds you combined.',
  'combine-more-tens': 'Check how many tens you combined.',
  'combine-more-ones': 'Check how many ones you combined.',
  'regroup-place-values': 'Can 10 pieces trade for 1 in the next place?',
  'check-place-values': 'Check each place-value channel.',
};

export interface NumberForgeHint {
  readonly stage: 1 | 2 | 3;
  readonly focus: NumberForgePlace;
  readonly message: string;
}

export interface NumberForgeSnapshot {
  readonly phase: NumberForgePhase;
  readonly sourceA: NumberForgePlaceCounts;
  readonly sourceB: NumberForgePlaceCounts;
  readonly revealedSources: readonly NumberForgeSource[];
  readonly channels: NumberForgePlaceCounts;
  readonly channelValue: number;
  readonly target: number;
  readonly hintStage: 0 | 1 | 2 | 3;
  readonly hint?: NumberForgeHint;
  readonly isComplete: boolean;
}

type MutablePlaceCounts = Record<NumberForgePlace, number>;

const PLACES_HIGH_TO_LOW: readonly NumberForgePlace[] = ['hundreds', 'tens', 'ones'];

function mutableCounts(value = 0): MutablePlaceCounts {
  return { hundreds: value, tens: value, ones: value };
}

function cloneCounts(counts: MutablePlaceCounts): NumberForgePlaceCounts {
  return { hundreds: counts.hundreds, tens: counts.tens, ones: counts.ones };
}

export class NumberForgeState {
  private sourceA: MutablePlaceCounts;
  private sourceB: MutablePlaceCounts;
  private channels = mutableCounts();
  private readonly revealedSources = new Set<NumberForgeSource>();
  private currentHint?: NumberForgeHint;
  private hintStage: 0 | 1 | 2 | 3 = 0;
  private completed = false;

  constructor(readonly config: NumberForgeConfig) {
    validateNumberForgeConfig(config);
    this.sourceA = { ...decomposeNumberForgeValue(config.addendA) };
    this.sourceB = { ...decomposeNumberForgeValue(config.addendB) };
  }

  moveToChannel(
    source: NumberForgeSource,
    place: NumberForgePlace,
    amount = 1,
  ): NumberForgeMoveResult {
    if (!this.canChange(amount, place)) return 'invalid';
    if (!this.revealedSources.has(source)) return 'source-hidden';
    const sourceCounts = source === 'a' ? this.sourceA : this.sourceB;
    if (sourceCounts[place] < amount) return 'empty-source';

    sourceCounts[place] -= amount;
    this.channels[place] += amount;
    this.clearHint();
    return this.phase === 'ready-to-charge' ? 'ready' : 'accepted';
  }

  returnToSource(
    source: NumberForgeSource,
    place: NumberForgePlace,
    amount = 1,
  ): NumberForgeMoveResult {
    if (!this.canChange(amount, place) || this.channels[place] < amount) return 'invalid';
    if (!this.revealedSources.has(source)) return 'source-hidden';
    const sourceCounts = source === 'a' ? this.sourceA : this.sourceB;
    this.channels[place] -= amount;
    sourceCounts[place] += amount;
    this.clearHint();
    return 'accepted';
  }

  regroup(place: 'ones' | 'tens'): NumberForgeMoveResult {
    if (this.completed || this.config.regrouping === 'none') return 'invalid';
    const destination: NumberForgePlace = place === 'ones' ? 'tens' : 'hundreds';
    if (!this.config.activePlaces.includes(destination) || this.channels[place] < 10) return 'invalid';

    this.channels[place] -= 10;
    this.channels[destination] += 1;
    this.clearHint();
    return this.phase === 'ready-to-charge' ? 'ready' : 'accepted';
  }

  revealSource(source: NumberForgeSource): NumberForgeMoveResult {
    if (this.completed || this.revealedSources.has(source)) return 'invalid';
    this.revealedSources.add(source);
    this.clearHint();
    return 'accepted';
  }

  charge(): NumberForgeChargeResult {
    if (this.completed) return 'complete';
    if (this.revealedSources.size < 2) return 'open-number-ingots';

    const incompletePlace = this.firstIncompletePlace;
    if (incompletePlace) return `combine-more-${incompletePlace}`;
    if (this.requiresExchange) return 'regroup-place-values';
    if (!this.matchesTarget) return 'check-place-values';

    this.completed = true;
    this.clearHint();
    return 'complete';
  }

  requestHint(): NumberForgeHint {
    this.hintStage = Math.min(3, this.hintStage + 1) as 1 | 2 | 3;
    const hiddenSource = (['a', 'b'] as const).find(source => !this.revealedSources.has(source));
    if (hiddenSource) {
      const value = hiddenSource === 'a' ? this.config.addendA : this.config.addendB;
      const hiddenMessages: Record<1 | 2 | 3, string> = {
        1: 'Open an ingot to discover its tens and ones.',
        2: `Open the ${value} number-energy ingot next.`,
        3: `Tap the blue-and-gold ingot labeled ${value}.`,
      };
      this.currentHint = {
        stage: this.hintStage,
        focus: 'tens',
        message: hiddenMessages[this.hintStage],
      };
      return this.currentHint;
    }
    const focus = this.firstIncompletePlace ?? (this.requiresExchange ? 'ones' : 'tens');
    const a = this.sourceA[focus];
    const b = this.sourceB[focus];
    const label = focus === 'hundreds' ? 'hundreds' : focus;
    const messages: Record<1 | 2 | 3, string> = {
      1: `Combine one place at a time. Try the ${label} channel.`,
      2: `Move the remaining ${label} pieces into the ${label} channel.`,
      3: a + b > 0
        ? `Combine ${a} and ${b} more ${label} pieces.`
        : 'Trade 10 ones for 1 ten, then check the forge.',
    };
    this.currentHint = { stage: this.hintStage, focus, message: messages[this.hintStage] };
    return this.currentHint;
  }

  reset() {
    this.sourceA = { ...decomposeNumberForgeValue(this.config.addendA) };
    this.sourceB = { ...decomposeNumberForgeValue(this.config.addendB) };
    this.channels = mutableCounts();
    this.revealedSources.clear();
    this.currentHint = undefined;
    this.hintStage = 0;
    this.completed = false;
  }

  get phase(): NumberForgePhase {
    if (this.completed) return 'complete';
    if (this.revealedSources.size < 2) return 'discovering';
    if (this.firstIncompletePlace) return 'sorting';
    if (this.requiresExchange) return 'regrouping';
    return 'ready-to-charge';
  }

  get snapshot(): NumberForgeSnapshot {
    return {
      phase: this.phase,
      sourceA: cloneCounts(this.sourceA),
      sourceB: cloneCounts(this.sourceB),
      revealedSources: [...this.revealedSources],
      channels: cloneCounts(this.channels),
      channelValue: this.channelValue,
      target: this.config.target,
      hintStage: this.hintStage,
      hint: this.currentHint,
      isComplete: this.completed,
    };
  }

  private canChange(amount: number, place: NumberForgePlace) {
    return !this.completed
      && Number.isInteger(amount)
      && amount > 0
      && this.config.activePlaces.includes(place);
  }

  private clearHint() {
    this.currentHint = undefined;
  }

  private get firstIncompletePlace(): NumberForgePlace | undefined {
    return PLACES_HIGH_TO_LOW.find(place => (
      this.config.activePlaces.includes(place)
      && this.sourceA[place] + this.sourceB[place] > 0
    ));
  }

  private get requiresExchange() {
    if (this.config.regrouping === 'none') return false;
    return this.channels.ones >= 10 || this.channels.tens >= 10;
  }

  private get matchesTarget() {
    const target = decomposeNumberForgeValue(this.config.target);
    return PLACES_HIGH_TO_LOW.every(place => this.channels[place] === target[place]);
  }

  private get channelValue() {
    return PLACES_HIGH_TO_LOW.reduce(
      (total, place) => total + this.channels[place] * NUMBER_FORGE_PLACE_VALUES[place],
      0,
    );
  }
}
